import { DateTime } from "luxon";
import {
  AiCreditBalanceLevel
} from "../../entities/creditUsage.js";
import {
  isUsageBasedFeatureKey
} from "../aiFeatureCatalog.js";
import { findOpenAIModelPricing } from "../openAITokenUsage.js";
const USD_PER_AI_CREDIT = 0.1;
const AI_CREDIT_BILLING_EARLIEST_START_DATE = "2026-11-01";
const AI_CREDITS_USED_UP_MESSAGE = "AI credits are used up";
function checkAiFeaturesPaused(settings, now = DateTime.utc()) {
  return resolveStoredAiCreditStatus(settings, now)?.level === AiCreditBalanceLevel.Exhausted;
}
function resolveStoredAiCreditStatus(settings, now = DateTime.utc()) {
  const status = settings.ai_credit_status;
  return status && now < DateTime.fromISO(status.resets_at) ? status : null;
}
const TOKENS_PER_AI_CREDIT_BY_PRICE_GROUP = {
  signals: 22e4,
  // ~$0.0050 per match
  research_agent: 135e3,
  // ~$0.71 per question
  "scheduled_agents:light": 25e4,
  // ~$0.40 per million tokens
  "scheduled_agents:full": 15e3,
  // ~$6.67 per million tokens
  mcp_tool_call: 1e5,
  // neutral: ~$1 per million tokens
  data_extraction: 22e4
  // ~$0.45 per million tokens
};
const AI_USAGE_PRICE_GROUPS = Object.keys(
  TOKENS_PER_AI_CREDIT_BY_PRICE_GROUP
);
const BUDGET_MODEL_INPUT_USD_PER_MILLION_TOKENS = 1;
function isBudgetModel(model) {
  const pricing = model ? findOpenAIModelPricing(model) : null;
  return pricing !== null && pricing.costPer1mInputTokensUsd < BUDGET_MODEL_INPUT_USD_PER_MILLION_TOKENS;
}
function resolveAiUsagePriceGroup(featureKey, model) {
  if (featureKey === "scheduled_agents") {
    return isBudgetModel(model) ? "scheduled_agents:light" : "scheduled_agents:full";
  }
  return featureKey;
}
function splitAiUsagePriceGroup(groupValue) {
  const [featureKey, tier] = groupValue.split(":");
  return isUsageBasedFeatureKey(featureKey) ? { featureKey, tier } : null;
}
function sumCreditsByFeature(creditsByGroupValue) {
  const creditsByFeature = /* @__PURE__ */ new Map();
  for (const [groupValue, credits] of creditsByGroupValue) {
    const featureKey = splitAiUsagePriceGroup(groupValue)?.featureKey;
    if (featureKey !== void 0) {
      creditsByFeature.set(
        featureKey,
        (creditsByFeature.get(featureKey) ?? 0) + credits
      );
    }
  }
  return creditsByFeature;
}
function convertTokensPerCreditToUsdPerMillionTokens(tokensPerCredit) {
  return USD_PER_AI_CREDIT * 1e6 / tokensPerCredit;
}
function buildPricingTotals(customerCostUsd, costToServeUsd) {
  const marginUsd = customerCostUsd - costToServeUsd;
  return {
    customerCostUsd,
    costToServeUsd,
    marginUsd,
    marginRatio: customerCostUsd === 0 ? null : marginUsd / customerCostUsd
  };
}
const AiUsageOperationKeyPrefix = {
  Signals: "signals:",
  CopilotTurn: "research_agent:turn:",
  ScheduledAgentsOrchestration: "scheduled_agents:orchestration:",
  ScheduledAgentsSubagent: "scheduled_agents:subagent:",
  McpToolCall: "mcp_tool_call:",
  DataExtraction: "data_extraction:"
};
function buildAiUsageOperationKey(prefix, ...operationIds) {
  return `${prefix}${operationIds.join(":")}`;
}
export {
  AI_CREDITS_USED_UP_MESSAGE,
  AI_CREDIT_BILLING_EARLIEST_START_DATE,
  AI_USAGE_PRICE_GROUPS,
  AiUsageOperationKeyPrefix,
  TOKENS_PER_AI_CREDIT_BY_PRICE_GROUP,
  USD_PER_AI_CREDIT,
  buildAiUsageOperationKey,
  buildPricingTotals,
  checkAiFeaturesPaused,
  convertTokensPerCreditToUsdPerMillionTokens,
  resolveAiUsagePriceGroup,
  resolveStoredAiCreditStatus,
  splitAiUsagePriceGroup,
  sumCreditsByFeature
};
//# sourceMappingURL=aiCredits.js.map
